import { Checkbox, InputLabel, MenuItem, Select } from '@mui/material';
import { BrandMenu, BrandMenuItem } from '@prismamedia/one-components';
import { InputGroup } from '@prismamedia/one-crud-react';
import { BrandKey } from '__generated__/queries-web';
import { CategoriesSelect } from 'components/CategoriesSelect';
import { RecipeCategoriesSelect } from 'components/CategoriesSelect/RecipeCategoriesSelect';
import {
  resources,
  ResourceType,
} from 'pages/CampaignPage/CampaignEditPage/ContentPage/utils/useGetBlockItems';
import React, { FC } from 'react';
import { AutomationConfigType } from '../';
import { useStyles } from '../../styles';
import { SourcesCheckboxes } from '../SourcesCheckboxes';
import { TagsSelect } from '../TagsSelect';
import { RecipeTagsSelect } from '../TagsSelect/RecipeTagSelect';
import { articleNumberOptions, dateRangeOptions } from '../utils';

interface AutomationConfigProps {
  isFromNlf: boolean;
  config: AutomationConfigType;
  brandKey: BrandKey;
  onConfigChange: (
    field: keyof AutomationConfigType,
  ) => (value?: string | string[] | number | boolean) => void;
}

export const AutomationConfig: FC<AutomationConfigProps> = ({
  isFromNlf,
  config = {},
  brandKey,
  onConfigChange,
}) => {
  const classes = useStyles();

  const initialBrandMenuItems: BrandMenuItem[] = React.useMemo(
    () => [
      {
        label: '-',
        id: ResourceType.EMPTY,
        selected: true,
      },
      ...resources.map(({ label, type }) => {
        return {
          label,
          id: type,
          selected: false,
          disabled:
            type === ResourceType.RECIPE &&
            brandKey !== BrandKey.FAC &&
            brandKey !== BrandKey.CAC,
        };
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [brandMenuItems, setBrandMenuItems] = React.useState<BrandMenuItem[]>(
    initialBrandMenuItems,
  );

  const [selectedResourceType, setSelectedResourceType] = React.useState<
    ResourceType | undefined
  >(undefined);

  const handleBrandMenu = (selectedBrandMenuItemId: ResourceType) => {
    const updatedBrandMenuItems = initialBrandMenuItems.map(
      (brandMenuItem) => ({
        ...brandMenuItem,
        selected: brandMenuItem.id === selectedBrandMenuItemId,
      }),
    );

    onConfigChange('type')(selectedBrandMenuItemId);
    setSelectedResourceType(selectedBrandMenuItemId);
    setBrandMenuItems(updatedBrandMenuItems);
  };

  React.useEffect(() => {
    config.type && handleBrandMenu(config.type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.type]);

  return (
    <>
      <InputLabel className={classes.label}>Type de ressource</InputLabel>
      <BrandMenu
        dark
        items={brandMenuItems}
        onItemSelect={(brandMenuItem) =>
          handleBrandMenu(brandMenuItem.id as ResourceType)
        }
      />
      {selectedResourceType && selectedResourceType !== ResourceType.EMPTY && (
        <>
          <InputGroup className={classes.spacingTop}>
            <InputLabel className={classes.label}>
              Provenance des articles *
            </InputLabel>
            <div className={classes.withoutAnalyticsRadios}>
              <Checkbox
                checked={!config.withoutAnalytics}
                color="primary"
                onChange={() => onConfigChange('withoutAnalytics')(false)}
              />
              <span>Google Analytics (Les plus vus)</span>
              <Checkbox
                checked={!!config.withoutAnalytics}
                color="primary"
                onChange={() => onConfigChange('withoutAnalytics')(true)}
              />
              <span>Les plus récemment publiés</span>
            </div>
          </InputGroup>
          <InputGroup className={classes.spacingTop}>
            <InputLabel id="automation-day-range" className={classes.label}>
              Période de récupération *
            </InputLabel>
            <Select
              labelId="automation-day-range"
              onChange={(e) =>
                onConfigChange('dayRange')(e.target.value as number)
              }
              value={config.dayRange || ''}
              variant="standard"
            >
              {dateRangeOptions.map(({ value, label }) => (
                <MenuItem key={label} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </InputGroup>
          <InputGroup className={classes.spacingTop}>
            <InputLabel id="automation-article-count" className={classes.label}>
              Nombre d'articles sélectionnés
            </InputLabel>
            <Select
              labelId="automation-article-count"
              value={config.articleCount || ''}
              onChange={(e) =>
                onConfigChange('articleCount')(e.target.value as number)
              }
              variant="standard"
              inputProps={{ disabled: isFromNlf }}
            >
              {articleNumberOptions.map(({ value, label }) => (
                <MenuItem key={label} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </InputGroup>
          {selectedResourceType === ResourceType.RECIPE ? (
            <>
              <InputGroup className={classes.spacingTop}>
                <RecipeCategoriesSelect
                  brandKey={brandKey}
                  onChange={(item) => onConfigChange('categoryIds')(item)}
                  automationConfig={config}
                />
              </InputGroup>
              <InputGroup className={classes.spacingTop}>
                <RecipeTagsSelect
                  brandKey={brandKey}
                  label="Filtrer par tags"
                  onChange={onConfigChange('tagIds')}
                  value={config.tagIds}
                />
              </InputGroup>
            </>
          ) : (
            <>
              <InputGroup className={classes.spacingTop}>
                <CategoriesSelect
                  onChange={(item) => onConfigChange('categoryIds')(item)}
                  automationConfig={config}
                  brandKey={brandKey}
                />
              </InputGroup>
              <InputGroup className={classes.spacingTop}>
                <TagsSelect
                  brandKey={brandKey}
                  label="Filtrer par tags"
                  onChange={onConfigChange('tagIds')}
                  value={config.tagIds}
                />
              </InputGroup>
            </>
          )}
          <InputGroup className={classes.spacingTop}>
            <SourcesCheckboxes
              brandKey={brandKey}
              onChange={onConfigChange('sourcesIds')}
              value={config.sourcesIds}
            />
          </InputGroup>
        </>
      )}
    </>
  );
};
